import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import ruiPhoto from '../../assets/images/rui16.jpg';

export function About() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const credentials = [
    t('about.credentials.doctor'),
    t('about.credentials.health'),
    t('about.credentials.pm'),
    t('about.credentials.hero'),
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative blob */}
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-sky-200/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-campaign-black mb-4 relative inline-block">
            {t('about.title')}
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-1 bg-sky-600" />
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src={ruiPhoto}
                alt="Dr. Rui Maria de Araújo"
                className="w-full rounded-xl shadow-2xl"
              />
            </div>
            <div className="absolute -top-4 -left-4 w-full h-full border-1 border-sky-400 rounded-xl z-0" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-sky-600 mb-6">
              {t('about.subtitle')}
            </h3>

            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {t('about.paragraph1')}
            </p>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {t('about.paragraph2')}
            </p>

            <div className="flex flex-wrap gap-4">
              {credentials.map((credential) => (
                <div
                  key={credential}
                  className="flex items-center gap-2 text-campaign-black font-semibold"
                >
                  <Check className="text-sky-600" size={20} />
                  {credential}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}